'use client';

import { useMemo, useRef, useState } from 'react';
import {
  Menu,
  BodyDefault,
  Badge,
  LabelDefault,
} from '@terra/ui-design-system';
import { Box } from '@terra/ui-styled-system';
import { ArrowRight01Icon, ArrowDownRight01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { UserTenant } from '@terra/shared-contracts-gateway-auth';
import { useUserTenants } from '../../../hooks/api/useUserTenants';
import { useSwitchTenant } from '../../../hooks/useSwitchTenant';
import { MenuDivider } from '../MenuDivider';
import { SidebarSubmenu } from '../SidebarSubmenu';
import {
  buildTenantHierarchy,
  type TenantHierarchyItem,
} from './buildTenantHierarchy';
import { SEARCH_VISIBILITY_THRESHOLD } from './constants';
import { TargetSelectorMenuItemRow } from './TargetSelectorMenuItemRow';
import { TargetSelectorSearchInput } from './TargetSelectorSearchInput';

function mapTenantsToHierarchyItems(
  tenants: UserTenant[],
): TenantHierarchyItem[] {
  return tenants.map((tenant) => ({
    id: tenant.id,
    name: tenant.name,
    roles: tenant.roles,
    isSubTenant: tenant.isSubTenant,
    parentId: tenant.parentId,
  }));
}

export function TenantSwitchMenuContent() {
  const t = useTranslations('layout');
  const { tenants, currentTenantId } = useUserTenants();
  const [searchQuery, setSearchQuery] = useState('');
  const { switchTenant, isSwitching } = useSwitchTenant();
  const tenantItems = useMemo(
    () => mapTenantsToHierarchyItems(tenants),
    [tenants],
  );
  const hierarchy = useMemo(
    () => buildTenantHierarchy(tenantItems),
    [tenantItems],
  );
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredHierarchy = useMemo(() => {
    if (!normalizedQuery) {
      return hierarchy;
    }

    return hierarchy.flatMap((parentTenant) => {
      const isParentMatch = parentTenant.name
        .toLowerCase()
        .includes(normalizedQuery);

      if (isParentMatch) {
        return [parentTenant];
      }

      const matchingChildren = parentTenant.children.filter((childTenant) =>
        childTenant.name.toLowerCase().includes(normalizedQuery),
      );

      if (matchingChildren.length === 0) {
        return [];
      }

      return [{ ...parentTenant, children: matchingChildren }];
    });
  }, [hierarchy, normalizedQuery]);
  const isLongList = tenantItems.length > SEARCH_VISIBILITY_THRESHOLD;
  const flatTenants = useMemo(
    () =>
      filteredHierarchy.flatMap((parent) => [
        { tenant: parent, isSubTenant: false },
        ...parent.children.map((child) => ({
          tenant: child,
          isSubTenant: true,
        })),
      ]),
    [filteredHierarchy],
  );
  const currentTenantEntry = useMemo(
    () => flatTenants.find(({ tenant }) => tenant.id === currentTenantId),
    [flatTenants, currentTenantId],
  );
  const currentTenant = currentTenantEntry?.tenant;
  const listTenants = useMemo(
    () =>
      !normalizedQuery && currentTenant
        ? flatTenants.filter(({ tenant }) => tenant.id !== currentTenant.id)
        : flatTenants,
    [flatTenants, currentTenant, normalizedQuery],
  );
  const showPinnedCurrent = !normalizedQuery && Boolean(currentTenantEntry);
  const renderTenantItem = ({
    tenant,
    isSubTenant,
    isCurrent,
  }: {
    tenant: TenantHierarchyItem;
    isSubTenant: boolean;
    isCurrent: boolean;
  }) => (
    <Menu.Item
      key={tenant.id}
      value={tenant.id}
      disabled={isSwitching}
      onClick={() => void switchTenant(tenant.id)}
      p="2"
    >
      <TargetSelectorMenuItemRow
        icon={isSubTenant ? <ArrowDownRight01Icon size={14} /> : undefined}
        iconColor="fg.neutral.muted"
        label={tenant.name}
        description={isSubTenant ? t('subTenant') : undefined}
        descriptionColor="fg.neutral.muted"
        trailing={
          tenant.roles[0] ? (
            <Badge size="sm" variant="emphasis">
              {tenant.roles[0]}
            </Badge>
          ) : undefined
        }
        showCheck={isCurrent}
      />
    </Menu.Item>
  );

  return (
    <>
      <Box px="2" py="1">
        <LabelDefault as="span" color="fg.neutral.muted" data-bi-unmask="true">
          {`${t('tenants')} (${tenantItems.length})`}
        </LabelDefault>
      </Box>
      {isLongList && (
        <Box px="2" pb="1">
          <TargetSelectorSearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t('searchTenants')}
          />
        </Box>
      )}
      <MenuDivider />
      {showPinnedCurrent && currentTenantEntry ? (
        <>
          <Menu.ItemGroup>
            {renderTenantItem({
              tenant: currentTenantEntry.tenant,
              isSubTenant: currentTenantEntry.isSubTenant,
              isCurrent: true,
            })}
          </Menu.ItemGroup>
          <MenuDivider />
        </>
      ) : null}
      {listTenants.length > 0 ? (
        <Menu.ItemGroup
          {...(isLongList ? { maxH: '280px', overflowY: 'auto' as const } : {})}
          gap="0"
        >
          {listTenants.map(({ tenant, isSubTenant }) =>
            renderTenantItem({
              tenant,
              isSubTenant,
              isCurrent: tenant.id === currentTenantId,
            }),
          )}
        </Menu.ItemGroup>
      ) : showPinnedCurrent ? null : (
        <Box px="3" py="3" textAlign="center">
          <BodyDefault color="fg.neutral.muted">
            {normalizedQuery ? t('noMatchingTenants') : t('switchTenant')}
          </BodyDefault>
        </Box>
      )}
    </>
  );
}

export function TenantSwitchSubmenu() {
  const t = useTranslations('layout');
  const triggerRef = useRef<HTMLDivElement>(null);

  return (
    <SidebarSubmenu
      triggerRef={triggerRef}
      trigger={
        <Menu.TriggerItem ref={triggerRef} p="2">
          <BodyDefault flexGrow={1}>{t('switchTenant')}</BodyDefault>
          <Menu.Indicator>
            <ArrowRight01Icon size={14} />
          </Menu.Indicator>
        </Menu.TriggerItem>
      }
    >
      <TenantSwitchMenuContent />
    </SidebarSubmenu>
  );
}
